import React, {useEffect, useRef, useState} from 'react'
import {Link as RouterLink, useParams} from 'react-router-dom'
import {
  DayItem,
  DayItems,
  DayItemText,
  Divider,
  HeaderTitle,
  NextDay,
  ObjectiveTitle,
  SidenavBox,
  SidenavExpandedContent,
  SidenavToggleBox,
  StyledIcon,
} from './styles'
import {InProgress} from '../../pages/StudyGuidePage/styles'

import SidenavHideIcon from '../../../images/icons/new-dashboard/sidenav-hide-icon.svg'
import SidenavExpandIcon from '../../../images/icons/new-dashboard/sidenav-expand-icon.svg'
import VideoIcon from '../../../images/icons/new-dashboard/video-icon.svg'
import VideoIconActive from '../../../images/icons/new-dashboard/live-video.svg'
import QuizIcon from '../../../images/icons/new-dashboard/quiz-icon.svg'
import QuizIconActive from '../../../images/icons/new-dashboard/quiz-active-icon.svg'

import FlashcardIcon from '../../../images/icons/new-dashboard/flashcard-icon.svg'
import FlashcardIconActive from '../../../images/icons/new-dashboard/flashcard-active-icon.svg'

import SlidesIcon from '../../../images/icons/new-dashboard/slides-icon.svg'
import SlidesIconActive from '../../../images/icons/new-dashboard/slides-icon-active.svg'

import ExamsIcon from '../../../images/icons/new-dashboard/exam-icon.svg'
import ExamsIconActive from '../../../images/icons/new-dashboard/exam-active-icon.svg'

import BreakIcon from '../../../images/icons/new-dashboard/break-icon.svg'
import BreakIconActive from '../../../images/icons/new-dashboard/break-active-icon.svg'

import NextDayIcon from '../../../images/icons/new-dashboard/next-day-icon.svg'
import NextDayIconActive from '../../../images/icons/new-dashboard/next-day-icon-active.svg'

import CompleteIcon from '../../../images/icons/new-dashboard/complete-icon.svg'
import {
  StudyGuide,
  StudyGuideAssets,
  StudyGuideStudyDays,
} from '../../../redux/studyGuides'
import {LinkBox} from '../../../styles/global'
import CompletedDayDialog from '../../../components/CompletedDayDialog'
import {formatDurationToHHMMSS} from '../../../utils/formatDate'
import {useWindowDimensions} from '../../../hooks/useWindowDimensions'

type Props = {
  currentAssetId?: string
  studyGuide?: StudyGuide
  studyDay?: number
  onSaveProgress: (value: number) => void
  isExpanded: boolean
  onChangeIsExpanded: (value: boolean) => void
}

const View = ({
  currentAssetId,
  studyGuide,
  studyDay,
  onSaveProgress,
  isExpanded,
  onChangeIsExpanded,
}: Props) => {
  const {isMobile} = useWindowDimensions()
  const {videoId} = useParams()

  const [height, setHeight] = useState(0)
  const [isDayCompleteDialogOpen, setIsDayCompleteDialogOpen] = useState(false)
  const [nextDayHover, setNextdayHover] = useState(false)

  const contentRef = useRef<HTMLDivElement | null>(null)

  const getIcon = (
    asset: StudyGuideAssets,
    hover: boolean,
    isActive: boolean,
  ) => {
    if (
      asset.assetType === 'quiz' &&
      (isActive || hover) &&
      !asset.isComplete
    ) {
      return <StyledIcon src={QuizIconActive} alt="Active Quiz" />
    }

    if (
      asset.assetType === 'video' &&
      (isActive || hover) &&
      !asset.isComplete
    ) {
      return <StyledIcon src={VideoIconActive} alt="Active Video" />
    }

    if (
      asset.assetType === 'flashcard_deck' &&
      (isActive || hover) &&
      !asset.isComplete
    ) {
      return <StyledIcon src={FlashcardIconActive} alt="Active Flahscard" />
    }

    if (
      asset.assetType === 'lecture_slides' &&
      (isActive || hover) &&
      !asset.isComplete
    ) {
      return <StyledIcon src={SlidesIconActive} alt="Active Slides" />
    }

    if (
      asset.assetType === 'practice_exam' &&
      (isActive || hover) &&
      !asset.isComplete
    ) {
      return <StyledIcon src={ExamsIconActive} alt="Active Exam" />
    }

    if (
      asset.assetType === 'break' &&
      (isActive || hover) &&
      !asset.isComplete
    ) {
      return <StyledIcon src={BreakIconActive} alt="Break" />
    }

    if (asset.isComplete) {
      return <StyledIcon src={CompleteIcon} alt="Completed" />
    }

    if (asset.assetType === 'quiz' && !isActive) {
      return <StyledIcon src={QuizIcon} alt="Quiz" />
    }

    if (asset.assetType === 'video' && !isActive) {
      return <StyledIcon src={VideoIcon} alt="Video" />
    }

    if (asset.assetType === 'flashcard_deck' && !isActive) {
      return <StyledIcon src={FlashcardIcon} alt="Flashcards" />
    }

    if (asset.assetType === 'lecture_slides' && !isActive) {
      return <StyledIcon src={SlidesIcon} alt="Lecture Slides" />
    }

    if (asset.assetType === 'practice_exam' && !isActive) {
      return <StyledIcon src={ExamsIcon} alt="Practice Exam" />
    }

    if (asset.assetType === 'break' && !isActive) {
      return <StyledIcon src={BreakIcon} alt="Break" />
    }
  }

  const getUnifiedAssets = (assets?: StudyGuideStudyDays) => {
    return assets
      ? [
          ...(Array.isArray(assets.video) ? assets.video : []),
          ...(Array.isArray(assets.quiz) ? assets.quiz : []),
          ...(Array.isArray(assets.flashcard_deck)
            ? assets.flashcard_deck
            : []),
          ...(Array.isArray(assets.lecture_slides)
            ? assets.lecture_slides
            : []),
          ...(Array.isArray(assets.practice_exam) ? assets.practice_exam : []),
          ...(Array.isArray(assets.break) ? assets.break : []),
        ]
      : []
  }
  const dayAssets = studyGuide?.assets?.find(a => a.studyDay === studyDay)
  const unifiedAssets = getUnifiedAssets(dayAssets)

  const nextDayAssets = studyGuide?.assets?.find(
    a => a.studyDay === (studyDay ?? 0) + 1,
  )
  const nextDayUnifiedAssets = getUnifiedAssets(nextDayAssets)

  const [hoverStates, setHoverStates] = useState<boolean[]>([])

  useEffect(() => {
    if (unifiedAssets && unifiedAssets.length !== hoverStates.length) {
      setHoverStates(Array(unifiedAssets.length).fill(false))
    }
  }, [unifiedAssets])

  const handleMouseEnter = (index: number) => {
    setHoverStates(
      prev => prev?.map((hover, i) => (i === index ? true : hover)),
    )
  }

  const handleMouseLeave = (index: number) => {
    setHoverStates(
      prev => prev?.map((hover, i) => (i === index ? false : hover)),
    )
  }

  const getNextAssetUrl = (asset?: StudyGuideAssets) => {
    let type = ''

    if (asset) {
      switch (asset.assetType) {
        case 'video':
          type = 'videos'
          break
        case 'quiz':
          type = 'quizzes'
          break
        case 'flashcard_deck':
          type = 'flashcards'
          break
        case 'lecture_slides':
          type = 'slides'
          break
        case 'break':
          type = 'breaks'
          break
        case 'practice_exam':
          type = 'practice-exams'
          break
      }

      return type === 'slides'
        ? asset.downloadUrl ?? ''
        : `/study-guide/${studyGuide?.id}/${asset?.studyDay}/${type}/${asset?.id}`
    }

    return ''
  }

  useEffect(() => {
    if (contentRef.current) {
      setHeight(contentRef.current.scrollHeight)
      if (isMobile) {
        onChangeIsExpanded(false)
      }
    }
  }, [studyGuide])

  useEffect(() => {
    if (dayAssets?.isCompleteOpen) {
      setIsDayCompleteDialogOpen(true)
    }
  }, [dayAssets?.isCompleteOpen])

  const handleSaveProgress = (asset: StudyGuideAssets) => {
    if (asset.assetType === 'lecture_slides') {
      onSaveProgress(asset.assetGlobalId)
      window.open(asset.downloadUrl)
    }
  }

  const currentAsset = unifiedAssets.find(
    ua => ua.id === (videoId ?? currentAssetId),
  )

  return (
    <SidenavBox $isExpanded={isExpanded} height={height} ref={contentRef}>
      <SidenavToggleBox
        onClick={() => onChangeIsExpanded(!isExpanded)}
        $isExpanded={isExpanded}
      >
        <img
          src={isExpanded ? SidenavHideIcon : SidenavExpandIcon}
          alt="Toggle Menu"
        />
        {isExpanded && <span>Hide menu</span>}
      </SidenavToggleBox>
      <SidenavExpandedContent>
        <HeaderTitle>
          {studyGuide?.abbreviation} - {studyGuide?.timeWindow} Days Study Plan
        </HeaderTitle>
        <Divider />
        <ObjectiveTitle>
          Day {studyDay}
          {dayAssets?.inProgress && <InProgress>In Progress</InProgress>}
        </ObjectiveTitle>
        {/* <ObjectiveText>
          Objective 1.1: Interpret the architect’s roles and responsibilities
          during pre-construction, based on delivery method
        </ObjectiveText> */}
        <DayItems>
          {unifiedAssets.map((asset, key) => {
            const isActive = asset.id === currentAssetId

            return (
              <React.Fragment key={`day-item-${key}`}>
                <Divider />
                <LinkBox component={RouterLink} to={getNextAssetUrl(asset)}>
                  <DayItem
                    $isActive={isActive}
                    $isComplete={asset.isComplete}
                    onMouseEnter={() => handleMouseEnter(key)}
                    onMouseLeave={() => handleMouseLeave(key)}
                    onClick={() => handleSaveProgress(asset)}
                  >
                    {getIcon(asset, hoverStates[key], isActive)}
                    <div>
                      <DayItemText $isLarge={true}>
                        {asset.assetTitle}
                      </DayItemText>
                      {asset.assetType !== 'break' && (
                        <DayItemText>
                          {asset.assetType === 'quiz'
                            ? asset.questions + ' Questions'
                            : formatDurationToHHMMSS(
                                Number(asset.duration) ?? 0,
                              )}
                        </DayItemText>
                      )}
                    </div>
                  </DayItem>
                </LinkBox>
              </React.Fragment>
            )
          })}
        </DayItems>
        {nextDayUnifiedAssets.length > 0 && (
          <LinkBox
            component={RouterLink}
            to={getNextAssetUrl(nextDayUnifiedAssets[0])}
          >
            <NextDay
              onMouseEnter={() => setNextdayHover(true)}
              onMouseLeave={() => setNextdayHover(false)}
            >
              Move to Next Day{' '}
              <img
                src={nextDayHover ? NextDayIconActive : NextDayIcon}
                alt="Move to next day"
              />
            </NextDay>
          </LinkBox>
        )}
      </SidenavExpandedContent>
      {studyDay &&
        studyGuide &&
        !['video', 'break'].includes(currentAsset?.assetType ?? '') && (
          <CompletedDayDialog
            open={isDayCompleteDialogOpen}
            setOpen={(value: boolean) => setIsDayCompleteDialogOpen(value)}
            studyDay={studyDay}
            studyGuide={studyGuide}
          />
        )}
    </SidenavBox>
  )
}

export default View
